import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { AgreementBalanceData, AgreementBillingData } from 'src/lib/fliko/models/reckonings/agreement.reck.data';
import { InvoiceData } from 'src/lib/fliko/models/reckonings/invoice.data';
import { EstateRentStructure, RentValue } from 'src/lib/fliko/models/reckonings/rent.data';
import { ResidentBalanceData, ResidentBillingData } from 'src/lib/fliko/models/reckonings/resident.reck.data';
import { UnitBalanceData, UnitBillingData } from 'src/lib/fliko/models/reckonings/unit.reck.data';
import { MetersUploadLog, ReckoningsUploadLog } from 'src/lib/fliko/models/reckonings/uploadLog.data';
import { Collection } from 'src/lib/fliko/types/database.type';
import { Nullable, Nullish } from 'src/lib/fliko/types/typeish.type';
import { DatabaseService } from 'src/app/shared/services/database.service';

@Injectable({ providedIn: 'root' })
export class ReckoningsService {
  constructor(private dbSrv: DatabaseService) {}

  // #region Agreement
  /**
   * @path /agreementBalance/$estateId/
   * @key agreementId
   */
  private get agreementBalance() {
    return this.dbSrv.estateFactory.getCollection<AgreementBalanceData>('agreementBalance/$eId');
  }

  public getAgreementBalances$(): Observable<AgreementBalanceData[]> {
    return this.agreementBalance.getList();
  }

  /**
   * @path /agreementBilling/$estateId/$agreementId
   * @key id
   */
  private agreementBilling = (agrId: string) =>
    this.dbSrv.estateFactory.getCollection<AgreementBillingData>(`agreementBilling/$eId/${agrId}`);

  public getAgreementBillings$(agreementId: string): Observable<AgreementBillingData[]> {
    return this.agreementBilling(agreementId).getList();
  }

  /**
   * @path /agreementBilling/$estateId
   * @key agreementId
   */
  private get allAgreementBillings() {
    return this.dbSrv.estateFactory.getCollection<Collection<AgreementBillingData>>('agreementBilling/$eId');
  }

  public getAllAgreementBillingsMap$(): Observable<Map<string, Collection<AgreementBillingData>>> {
    return this.allAgreementBillings.getObject().pipe(map((obj) => MapHlp.fromObject(obj)));
  }

  // #endregion Agreement

  // #region Unit
  /**
   * @path /unitBalance/$estateId
   * @key unitId
   */
  private get unitBalance() {
    return this.dbSrv.estateFactory.getCollection<UnitBalanceData>('unitBalance/$eId');
  }

  public getUnitBalances$(): Observable<UnitBalanceData[]> {
    return this.unitBalance.getList();
  }

  public getUnitBalance$(unitId: string): Observable<UnitBalanceData | Nullish> {
    return this.unitBalance.getElement(unitId);
  }

  public updateUnitBalance(unitId: string, data: Partial<UnitBalanceData>): Promise<void> {
    return this.unitBalance.updateElement(unitId, data);
  }

  /**
   * @path /unitBilling/$estateId/$unitId
   * @key id
   */
  private unitBilling = (unitId: string) => this.dbSrv.estateFactory.getCollection<UnitBillingData>(`unitBilling/$eId/${unitId}`);

  public getUnitBillings$(unitId: string): Observable<UnitBillingData[]> {
    return this.unitBilling(unitId).getList();
  }

  /**
   * @path /unitBilling/$estateId
   * @key unitId
   */
  private get allUnitBillings() {
    return this.dbSrv.estateFactory.getCollection<Collection<UnitBillingData>>('unitBilling/$eId');
  }

  public getAllUnitBillingsMap$(): Observable<Map<string, Collection<UnitBillingData>>> {
    return this.allUnitBillings.getObject().pipe(map((obj) => MapHlp.fromObject(obj)));
  }
  // #endregion Unit

  // #region Resident
  /**
   * @path /residentBalance/$estateId/
   * @key residentId
   */
  private get residentBalance() {
    return this.dbSrv.estateFactory.getCollection<ResidentBalanceData>('residentBalance/$eId');
  }

  public getResidentBalances$(): Observable<ResidentBalanceData[]> {
    return this.residentBalance.getList();
  }

  /**
   * @path /residentBilling/$estateId/$residentId
   * @key id
   */
  private residentBilling = (resId: string) =>
    this.dbSrv.estateFactory.getCollection<ResidentBillingData>(`residentBilling/$eId/${resId}`);

  public getResidentBillings$(residentId: string): Observable<ResidentBillingData[]> {
    return this.residentBilling(residentId).getList();
  }

  /**
   * @path /residentBilling/$estateId
   * @key residentId
   */
  private get allResidentBillings() {
    return this.dbSrv.estateFactory.getCollection<Collection<ResidentBillingData>>('residentBilling/$eId');
  }

  public getAllResidentBillingsMap$(): Observable<Map<string, Collection<ResidentBillingData>>> {
    return this.allResidentBillings.getObject().pipe(map((obj) => MapHlp.fromObject(obj)));
  }
  // #endregion Resident

  /**
   * @path /invoice/$estateId/
   * @key id
   */
  private get invoice() {
    return this.dbSrv.estateFactory.getCollection<InvoiceData>('invoice/$eId');
  }

  public getInvoices$(): Observable<InvoiceData[]> {
    return this.invoice.getList();
  }

  public getInvoice$(id: string): Observable<InvoiceData | Nullish> {
    return this.invoice.getElement(id);
  }

  // #region Rent
  /** @path /estateRentStructure/$estateId */
  private get estateRentStructure() {
    return this.dbSrv.estateFactory.getObject<EstateRentStructure>('estateRentStructure/$eId');
  }

  public getEstateRentStructure$(): Observable<Nullable<EstateRentStructure>> {
    return this.estateRentStructure.get();
  }

  /**
   * @path /rent/$estateId/unit
   * @key unitId
   */
  private get unitRent() {
    return this.dbSrv.estateFactory.getCollection<RentValue>('rent/$eId/unit');
  }

  public getUnitsRentMap$(): Observable<Map<string, RentValue>> {
    return this.unitRent.getMap();
  }

  /**
   * @path /rent/$estateId/unit
   * @key residentId
   */
  private get residentRent() {
    return this.dbSrv.estateFactory.getCollection<RentValue>('rent/$eId/resident');
  }

  public getResidentsRentMap$(): Observable<Map<string, RentValue>> {
    return this.residentRent.getMap();
  }
  // #endregion Rent

  // #region Upload Log

  /**
   * @path /uploadLog/meters/$estateId
   * @key id
   */
  private get metersUploadLog() {
    return this.dbSrv.estateFactory.getCollection<any>('uploadLog/meters/$eId');
  }

  public getMetersUploadLogs$(): Observable<MetersUploadLog[]> {
    return this.metersUploadLog.getList();
  }

  public setMetersUploadLog(log: MetersUploadLog): Promise<void> {
    return this.metersUploadLog.setElement(log.id, log);
  }

  /**
   * @path /uploadLog/reckonings/$estateId
   * @key id
   */
  private get reckoningsUploadLog() {
    return this.dbSrv.estateFactory.getCollection<any>('uploadLog/reckonings/$eId');
  }

  public getReckoningsUploadLogs$(): Observable<ReckoningsUploadLog[]> {
    return this.reckoningsUploadLog.getList();
  }

  public setReckoningsUploadLog(log: ReckoningsUploadLog): Promise<void> {
    return this.reckoningsUploadLog.setElement(log.id, log);
  }

  // #endregion Upload Log
}
